import React, { useCallback, useMemo, useState } from "react";
import { FieldArray, Formik as FormikBase, type FormikHelpers } from "formik";
import * as Yup from "yup";
import moment from "moment";
import type { Moment } from "moment";

import ExtendLocationsBase from "./extend-locations/ExtendLocations";
import { useNavigate } from "react-router-dom";
import { Button } from "@mui/material";
import CreativeSelectionBase from "./creative-selection/CreativeSelection";
import { extendCampaignHelper } from "../utils/extendCampaignHelper";
import ConfirmMessageBase from "./ConfirmMessage";
import RpAlertBase from "@/components/alerts/RpAlert";
import { RpSweetAlert } from "@/components/alerts/RpSweetAlert";

type Id = number | string;
type DateValue = string | Date | Moment | null | undefined;

type CampaignCreative = {
  id?: Id;
  is_selected?: boolean;
  [key: string]: unknown;
};

type ExtendEnrollment = {
  start_date?: DateValue;
  end_date?: DateValue;
};

type ExtendLocationData = {
  location_id: Id;
  location_name?: string;
  start_date?: DateValue;
  end_date?: DateValue;
  extend_end_date?: DateValue;
  total_budget?: number | string | null;
  creative_data?: CampaignCreative[];
  packages_data?: CampaignCreative[];
  subscription?: {
    subscription_end_date?: DateValue;
  } | null;
  extend_enrollments?: ExtendEnrollment[] | null;
  [key: string]: unknown;
};

type ExtendCampaignData = {
  creative_select_max_value?: number | string | null;
  overall_minimum_budget_extend?: number;
  daily_maximum_budget_extend?: number;
  daily_minimum_budget_extend?: number;
  evergreen_participant_min_value?: number | string | null;
  evergreen_participant_max_value?: number | string | null;
  availability_window?: string | null;
  availability_window_date?: {
    window_start_date?: DateValue;
    window_end_date?: DateValue;
  } | null;
  location_fees_covered?: boolean | null;
  contract_end_date?: DateValue;
  enable_payment_through_portal?: number | boolean | null;
  location_data?: ExtendLocationData[];
};

type SelectedLocationValue = {
  location_id?: Id;
  location_name?: string;
  start_date?: DateValue;
  end_date?: DateValue;
  budget?: number | string | null;
  creatives?: CampaignCreative[];
  locationInfo?: ExtendLocationData;
  extend_start_date?: DateValue;
  extend_end_date?: DateValue;
  extend_budget?: number | string | null;
  extend_creatives?: Id[];
  can_update_creatives?: boolean;
  [key: string]: unknown;
};

type ExtendSettingValues = {
  select_all: boolean;
  selectedLoc: Record<Id, SelectedLocationValue>;
  enable_payment_through_portal: number | boolean;
};

type ExtendSettingErrors = {
  selectedLoc?:
    | string
    | Record<Id, Record<string, string | undefined> | undefined>;
  [key: string]: unknown;
};

type ExtendSettingProps = {
  campaignData?: ExtendCampaignData;
  onContinueToConfirm: (_values: ExtendSettingValues) => void;
  step: number;
  setStep: (_step: number) => void;
};

const ExtendLocations = ExtendLocationsBase as React.ComponentType<
  Record<string, unknown>
>;
const CreativeSelection = CreativeSelectionBase as React.ComponentType<
  Record<string, unknown>
>;
const ConfirmMessage = ConfirmMessageBase as React.ComponentType<
  Record<string, unknown>
>;
const RpAlert = RpAlertBase as React.ComponentType<
  Record<string, unknown> & { children?: React.ReactNode }
>;
const Formik = FormikBase as unknown as React.ComponentType<
  Record<string, unknown> & { children?: unknown }
>;

const LOC_INIT_STATE: SelectedLocationValue = {
  location_id: "",
  location_name: "",
  start_date: "", // Ensure empty strings, not null
  end_date: "", // Ensure empty strings, not null
  budget: "",
  creatives: [],
  extend_start_date: "",
  extend_end_date: "",
  extend_budget: "",
  extend_creatives: [],

  // operational fields
  can_update_creatives: true,
};

// Validation schema for extend campaign
const createValidationSchema = (campaignData?: ExtendCampaignData) => {
  return Yup.object().shape({
    selectedLoc: Yup.object()
      .test(
        "at-least-one-location-filled",
        "Please complete selections for at least one location to continue.",
        function (selectedLoc) {
          if (!selectedLoc) return false;

          const selectedLocations = selectedLoc as Record<
            string,
            SelectedLocationValue
          >;
          const locationIds = Object.keys(selectedLocations);
          let hasAnyFilledLocation = false;

          for (const locationId of locationIds) {
            const location = selectedLocations[locationId];
            if (
              location.extend_start_date &&
              location.extend_end_date &&
              location.extend_budget
            ) {
              hasAnyFilledLocation = true;
              break;
            }
          }

          return hasAnyFilledLocation;
        }
      )
      .test(
        "valid-location-data",
        "Please fill all required fields for locations you want to extend",
        function (selectedLoc) {
          if (!selectedLoc) return true;

          const selectedLocations = selectedLoc as Record<
            string,
            SelectedLocationValue
          >;
          const locationIds = Object.keys(selectedLocations);
          const errors = [];

          for (const locationId of locationIds) {
            const location = selectedLocations[locationId];

            // Only validate if at least one field is filled
            const hasAnyField =
              location.extend_start_date ||
              location.extend_end_date ||
              location.extend_budget;

            if (!hasAnyField) continue; // Skip validation if no fields are filled

            const hasStartDate =
              (location.extend_start_date ?? "").toString().trim() !== "";
            const hasEndDate =
              (location.extend_end_date ?? "").toString().trim() !== "";
            const hasBudget =
              (location.extend_budget ?? "").toString().trim() !== "";

            // Show recommendation when dates are filled, even if budget is empty (triggered on end date selection)
            if (hasStartDate && hasEndDate) {
              const {
                overall_minimum_budget_extend,
                daily_maximum_budget_extend,
                daily_minimum_budget_extend,
              } = campaignData || {};

              const days = moment(location.extend_end_date).diff(
                moment(location.extend_start_date).subtract(1, "day"),
                "days"
              );

              const dailyMinimumBudget =
                Number(daily_minimum_budget_extend) * days;
              const dailyMaximumBudget =
                Number(daily_maximum_budget_extend) * days;

              const minPossibleBudget = Math.max(
                dailyMinimumBudget,
                Number(overall_minimum_budget_extend)
              );

              // Show recommendation when budget is empty (triggered when end date is selected)
              if (!hasBudget) {
                errors.push(
                  this.createError({
                    path: `selectedLoc.${locationId}.extend_budget`,
                    message: `We recommend a budget between $${minPossibleBudget} and $${dailyMaximumBudget}`,
                  })
                );
              }

              // Validate budget value if it exists
              if (hasBudget) {
                if (Number(location.extend_budget) < minPossibleBudget) {
                  errors.push(
                    this.createError({
                      path: `selectedLoc.${locationId}.extend_budget`,
                      message: `We recommend a budget between $${minPossibleBudget} and $${dailyMaximumBudget}`,
                    })
                  );
                }
                if (Number(location.extend_budget) > dailyMaximumBudget) {
                  errors.push(
                    this.createError({
                      path: `selectedLoc.${locationId}.extend_budget`,
                      message: `We recommend a budget between $${minPossibleBudget} and $${dailyMaximumBudget}`,
                    })
                  );
                }
              }
            }
          }

          // Throw all errors at once if any exist
          if (errors.length > 0) {
            throw new Yup.ValidationError(errors);
          }

          return true;
        }
      ),
  });
};

// Helper function to check if there are validation errors
const hasValidationErrors = (errors?: ExtendSettingErrors) => {
  if (!errors) return false;

  // Check if there are any field-level errors in the selectedLoc object
  if (errors.selectedLoc) {
    const locationIds = Object.keys(errors.selectedLoc);
    for (const locationId of locationIds) {
      const locationErrors = errors.selectedLoc[locationId];
      if (locationErrors && typeof locationErrors === "object") {
        const fieldErrors = Object.values(locationErrors);
        if (
          fieldErrors.some(
            (error) => typeof error === "string" && error.trim() !== ""
          )
        ) {
          return true;
        }
      }
    }
  }

  // Also check for any top-level errors
  const topLevelErrors = Object.values(errors).filter(
    (error) => typeof error === "string" && error.trim() !== ""
  );

  return topLevelErrors.length > 0;
};

function ExtendSetting({
  campaignData,
  onContinueToConfirm,
  step,
  setStep,
}: ExtendSettingProps) {
  const navigate = useNavigate();
  const [selections, setSelections] = useState<ExtendSettingValues | null>(
    null
  );

  const [creativeUpdateStatus, setCreativeUpdateStatus] = useState<
    string | null
  >(null);

  const initialValue = useMemo<ExtendSettingValues>(() => {
    // Create an object with location IDs as keys for easier lookup
    const selectedLoc: Record<Id, SelectedLocationValue> = {};
    campaignData?.location_data?.forEach((loc) => {
      const extendCampaignLocStartDate =
        extendCampaignHelper.getExtendCampaignLocStartDate(loc, campaignData);
      selectedLoc[loc.location_id] = {
        ...LOC_INIT_STATE,
        location_id: loc.location_id || "",
        location_name: loc.location_name || "",
        start_date: loc?.extend_end_date
          ? loc?.extend_end_date
          : loc?.start_date || "", // Ensure empty strings, not null
        end_date: loc?.end_date || "", // Ensure empty strings, not null
        budget: loc?.total_budget || "",
        creatives: loc?.creative_data || [],
        locationInfo: loc,
        extend_creatives:
          loc?.packages_data?.filter((c) => c.is_selected)?.map((c) => c.id) ||
          [],
        extend_start_date: extendCampaignLocStartDate || "",
      };
    });

    return {
      select_all: false,
      selectedLoc,
      enable_payment_through_portal:
        campaignData?.enable_payment_through_portal || 0,
    };
  }, [campaignData]);

  const handleSubmit = useCallback(
    (
      values: ExtendSettingValues,
      action: FormikHelpers<ExtendSettingValues>
    ) => {
      if (step === 2) {
        setSelections(null);

        // check if any location has no creative selected
        const filledLocData = Object.values(values.selectedLoc).filter(
          (value) => {
            return (
              value.extend_start_date &&
              value.extend_end_date &&
              value.extend_budget
            );
          }
        );
        const notFilledCreative = filledLocData?.some((value) => {
          return !value?.extend_creatives?.length;
        });

        if (notFilledCreative) {
          RpSweetAlert.fire({
            title: "Creative not selected",
            text: "There are some locations that have no creative selected. Please select a creative for these locations.",
            icon: "error",
            confirmButtonText: "OK",
            showCancelButton: false,
          });
          action.setSubmitting(false);
          return;
        }

        // Check if any location exceeds the maximum creatives allowed
        const creative_select_max_value =
          campaignData?.creative_select_max_value;
        // If max value is null → treat as 1
        const maxValue =
          creative_select_max_value === null
            ? 1
            : Number(creative_select_max_value);

        if (maxValue) {
          const locationExceedsMax = filledLocData?.some((value) => {
            return value?.extend_creatives?.length > maxValue;
          });

          if (locationExceedsMax) {
            RpSweetAlert.fire({
              icon: "info",
              html: `<p>You can only select <b>up to ${maxValue} creatives</b></p>`,
              showConfirmButton: true,
              confirmButtonText: "OK",
              confirmButtonColor: "var(--primary-base)",
              showCancelButton: false,
            });
            action.setSubmitting(false);
            return;
          }
        }

        onContinueToConfirm(values);
        action.setSubmitting(false);
        setStep(3);
        setCreativeUpdateStatus(null);
        return;
      }

      // Validate: Check if any selected dates overlap with extended ranges
      const locationData = campaignData?.location_data || [];
      const conflicts: Array<{
        locationName: string;
        conflictingRanges: Array<{
          start_date?: DateValue;
          end_date?: DateValue;
        }>;
      }> = [];

      Object.entries(values.selectedLoc || {}).forEach(
        ([locationId, locationValues]) => {
          if (
            !locationValues.extend_start_date ||
            !locationValues.extend_end_date
          ) {
            return;
          }

          const loc = locationData.find(
            (l) => Number(l.location_id) === Number(locationId)
          );
          if (!loc) return;

          const conflict =
            extendCampaignHelper.validateDateRangeAgainstExtendedRanges(
              locationValues.extend_start_date,
              locationValues.extend_end_date,
              loc
            );

          if (conflict) {
            conflicts.push(conflict);
          }
        }
      );

      if (conflicts.length > 0) {
        // Build conflict message
        const conflictMessages = conflicts.map((conflict) => {
          const locationName = conflict.locationName || "Location";
          const rangesText = conflict.conflictingRanges
            .map((range) => `${range.start_date} to ${range.end_date}`)
            .join(" and ");
          return `Location: ${locationName} already extended from ${rangesText}. Selected dates overlap this range. Please choose different dates.`;
        });

        RpSweetAlert.fire({
          title: "Date Range Conflict",
          html: conflictMessages.join("<br/><br/>"),
          icon: "error",
          confirmButtonText: "OK",
          showCancelButton: false,
        });
        action.setSubmitting(false);
        return;
      }

      const creativeUpdateStatus = extendCampaignHelper.getCreativeUpdateStatus(
        Object.values(values.selectedLoc) || []
      );
      setCreativeUpdateStatus(creativeUpdateStatus);
      setSelections(values);
      action.setSubmitting(false);
    },
    [step, onContinueToConfirm, campaignData, setStep]
  );

  const handleKeepCreative = useCallback(() => {
    if (selections) {
      setSelections(null);
      onContinueToConfirm(selections);
    }
  }, [onContinueToConfirm, selections]);

  const handleUpdateCreative = useCallback(() => {
    if (step === 1) {
      setSelections(null);
      setStep(2);
    }
  }, [setStep, step]);

  return (
    <>
      <Formik
        initialValues={initialValue}
        onSubmit={handleSubmit}
        enableReinitialize
        validationSchema={createValidationSchema(campaignData)}
        validateOnChange={true}
        debug
      >
        {({
          handleSubmit,
          errors,
          isSubmitting,
        }: {
          handleSubmit: React.FormEventHandler<HTMLFormElement>;
          errors: ExtendSettingErrors;
          isSubmitting: boolean;
        }) => {
          const formErrors = errors as ExtendSettingErrors;
          return (
            <form onSubmit={handleSubmit} className="mt-4">
              <FieldArray
                name="selectedLoc"
                render={(arrayHelpers) =>
                  step === 2 ? (
                    <CreativeSelection
                      {...arrayHelpers}
                      locationData={campaignData?.location_data}
                      //   campaignId={campaignId}
                      campaignData={campaignData}
                      LOC_INIT_STATE={LOC_INIT_STATE}
                    />
                  ) : (
                    <ExtendLocations
                      {...arrayHelpers}
                      locationData={campaignData?.location_data}
                      //   campaignId={campaignId}
                      campaignData={campaignData}
                      LOC_INIT_STATE={LOC_INIT_STATE}
                    />
                  )
                }
              />

              {formErrors?.selectedLoc &&
                typeof formErrors?.selectedLoc === "string" && (
                  <RpAlert
                    className="my-2"
                    variant="filled"
                    severity="error"
                    sx={{
                      fontSize: "12px",
                      py: 0,
                    }}
                  >
                    {formErrors.selectedLoc}
                  </RpAlert>
                )}

              <div className="d-flex align-items-center flex-wrap justify-content-center justify-content-lg-end">
                {step === 1 && (
                  <Button
                    className="px-4 mr-3"
                    variant="outlined"
                    color="primary"
                    onClick={() => navigate(-1)}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                )}
                {step === 2 && (
                  <Button
                    className="px-4 mr-3"
                    variant="outlined"
                    color="primary"
                    onClick={() => setStep(1)}
                    disabled={isSubmitting}
                  >
                    Back To Table
                  </Button>
                )}

                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  className="px-5"
                  disabled={
                    hasValidationErrors(formErrors) ||
                    isSubmitting ||
                    !campaignData?.location_data?.length
                  }
                >
                  {step === 1 ? "Continue" : "Confirm"}
                </Button>
              </div>
            </form>
          );
        }}
      </Formik>
      {creativeUpdateStatus && (
        <ConfirmMessage
          open={!!selections}
          creativeUpdateStatus={creativeUpdateStatus}
          setStep={setStep}
          onCancel={() => setCreativeUpdateStatus(null)}
          onKeepCreative={handleKeepCreative}
          onUpdateCreative={handleUpdateCreative}
        />
      )}
    </>
  );
}

export default ExtendSetting;
